/**
 * AP-18 5.1, 6. — Zustand der **Seitenleiste des Assistenten**: offen oder zu, welches Gespräch
 * sie zeigt, welcher Datensatz auf der Seite geöffnet ist und ob der Assistent Fragen annimmt.
 *
 * Nichts davon wird im Browser aufbewahrt: Gespräche liegen auf dem Server und gehören dem
 * Benutzer; wechselt er oder die Firma, wird der Zustand geleert (`zuruecksetzen`).
 */
import { useEffect } from 'react'
import { create } from 'zustand'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { assistentApi, type AssistentStand } from './assistent.api'

/** Der Datensatz, den eine Seite geöffnet hat — „diese Bestellung" meint ihn (Spezifikation 6.). */
export interface AssistentDatensatz {
  /** `document`, `project`, `calculation` … */
  art: string
  id: string
  /** Kurzname, wie ihn ein Mensch sagt: „Bestellung BE2026-0042 an Stahl Maier" */
  bezeichnung: string
  /** Kennungen der markierten Zeilen einer Liste */
  auswahl?: string[]
}

interface AssistentZustand {
  offen: boolean
  /** Gespräch der Seitenleiste; `null` = ein neues, das mit der ersten Frage entsteht */
  gespraechId: string | null
  /** Frage, die beim Öffnen gleich gestellt wird (Eingabefeld auf den Dashboards, 5.3) */
  vorgabe: string | null
  datensatz: AssistentDatensatz | null
  /** `null` = noch nicht geladen */
  stand: AssistentStand | null
  oeffnen: (a?: { frage?: string; gespraechId?: string | null }) => void
  schliessen: () => void
  umschalten: () => void
  setGespraech: (id: string | null) => void
  vorgabeVerbraucht: () => void
  setDatensatz: (d: AssistentDatensatz | null) => void
  ladeStand: () => Promise<void>
  zuruecksetzen: () => void
}

export const useAssistent = create<AssistentZustand>((set, get) => ({
  offen: false,
  gespraechId: null,
  vorgabe: null,
  datensatz: null,
  stand: null,
  oeffnen: (a) =>
    set((s) => ({
      offen: true,
      // Eine mitgegebene Frage beginnt ein neues Gespräch — sie gehört zum Ort, an dem sie gestellt wurde.
      gespraechId: a?.gespraechId !== undefined ? a.gespraechId : a?.frage ? null : s.gespraechId,
      vorgabe: a?.frage?.trim() || null,
    })),
  schliessen: () => set({ offen: false, vorgabe: null }),
  umschalten: () => (get().offen ? get().schliessen() : get().oeffnen()),
  setGespraech: (gespraechId) => set({ gespraechId }),
  vorgabeVerbraucht: () => set({ vorgabe: null }),
  setDatensatz: (datensatz) => set({ datensatz }),
  ladeStand: async () => {
    try {
      set({ stand: await assistentApi.stand() })
    } catch {
      // Ohne Stand bleibt die Eingabe offen; die Frage selbst meldet dann, woran es liegt.
      set({ stand: null })
    }
  },
  zuruecksetzen: () => set({ offen: false, gespraechId: null, vorgabe: null, datensatz: null, stand: null }),
}))

/** Hat dieser Benutzer den Assistenten? Baustein gebucht und Recht `ai.assistant.use` — Bequemlichkeit, die Sperre sitzt im Backend. */
export function useDarfAssistent(): boolean {
  const user = useAuthStore((s) => s.user)
  const hasModule = useAuthStore((s) => s.hasModule)
  return !!user && hasModule('AI_ASSISTANT') && erfuelltRecht('ai.assistant.use', user.permissions)
}

/**
 * Eine Seite meldet, welchen Datensatz sie geöffnet hat; beim Verlassen nimmt sie die Meldung
 * zurück. `null`, solange nichts geladen oder gewählt ist.
 *
 * ```tsx
 * useAssistentDatensatz(beleg ? { art: 'document', id: beleg.id, bezeichnung: `${art} ${beleg.number} an ${beleg.clientName}` } : null)
 * ```
 */
export function useAssistentDatensatz(datensatz: AssistentDatensatz | null) {
  const setDatensatz = useAssistent((s) => s.setDatensatz)
  const schluessel = datensatz ? `${datensatz.art}:${datensatz.id}:${datensatz.bezeichnung}:${(datensatz.auswahl ?? []).join(',')}` : ''
  useEffect(() => {
    setDatensatz(datensatz)
    return () => setDatensatz(null)
    // Der Schlüssel fasst den Inhalt zusammen — das Objekt selbst ist bei jedem Zeichnen neu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, setDatensatz])
}
